import { useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router'
import { SearchState } from '../state'
import { SearchStateManager } from '../types'

/**
 * Marshals reads/writes to the search params ensuring that values are always lists of
 * strings and supporting partial updates. This is the default state management if
 * no other state manager util is passed into the useSearch hook.
 * Default values are used if no key is found in the current search state,
 * otherwise they are ignored.
 *
 * @param defaultState default state to use if no key found in current search state
 * @returns the resolved search state and a function to update it (merged updates)
 */
const useSearchStateParams = (defaultState: SearchState): SearchStateManager => {
  const [searchParams, setSearchParams] = useSearchParams()

  const stateRef = useRef<SearchState>({})

  // Apply defaults first, then overlay URL params so that unknown params (e.g. ?hello=hi)
  // do not prevent default filters/sorting/pagination from being applied.
  const state: SearchState = useMemo(() => {
    const state: SearchState = { ...defaultState }

    const seenKeys = new Set<string>()
    for (const [key, value] of searchParams.entries()) {
      if (seenKeys.has(key)) {
        if (value !== '') state[key].push(value)
      } else {
        if (value !== '') {
          state[key] = [value]
        } else {
          state[key] = []
        }
      }
      seenKeys.add(key)
    }

    return state
  }, [searchParams, defaultState])

  stateRef.current = state

  const updateSearchState = useCallback(
    (stateUpdates: SearchState) => {
      const merged: SearchState = {
        ...stateRef.current,
        ...stateUpdates,
      }

      // Normalize "cleared" filters. A filter is cleared either as an empty array
      // (e.g. StaticSelect) or as `undefined` (e.g. boolean/date-range filters).
      // Empty arrays are dropped entirely when written to the URL and `undefined`
      // is serialized as the literal string "undefined" (key=undefined), neither
      // of which round-trips as a cleared value:
      //  - For a filter with a non-empty default, dropping it re-applies the
      //    default on the next read (e.g. the status filter resetting when the
      //    user types in the search box), so serialize it as [''] (key= in the
      //    URL) so the cleared state survives; it reads back as [] for consumers.
      //  - Otherwise remove the key so it does not linger in the URL (and never
      //    as the literal "undefined").
      for (const key of Object.keys(merged)) {
        const value = merged[key]
        const isCleared = value === undefined || (Array.isArray(value) && value.length === 0)
        if (!isCleared) continue

        if ((defaultState[key]?.length ?? 0) > 0) {
          merged[key] = ['']
        } else {
          delete merged[key]
        }
      }

      setSearchParams(merged, { replace: true })
    },
    [setSearchParams, defaultState],
  )

  return [state, updateSearchState]
}

export default useSearchStateParams
